import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Switch,
  VStack,
  useToast,
  Text,
  Divider
} from "@chakra-ui/react";
import { saApi } from "../../api/superAdminApi";

const CreateCompanyModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: "",
    cv_mailing_list: "",
    manager_name: "",
    billing_manager_name: "",
    billing_manager_email: "",
    website: "",
    trial_days: 14,
    is_active: false
  });
  const toast = useToast();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await saApi.createCompany(formData);
      toast({
        title: "Company created successfully",
        description: `Company "${response.company_name}" has been created with ID ${response.id}`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      onSuccess();
      onClose();
      // Reset form
      setFormData({
        company_name: "",
        cv_mailing_list: "",
        manager_name: "",
        billing_manager_name: "",
        billing_manager_email: "",
        website: "",
        trial_days: 14,
        is_active: false
      });
    } catch (error) {
      toast({
        title: "Error creating company",
        description: error.response?.data?.message || "Failed to create company",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>Create New Company</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Basic Information */}
            <Text fontSize="lg" fontWeight="bold">Basic Information</Text>
            <FormControl isRequired>
              <FormLabel>Company Name</FormLabel>
              <Input
                value={formData.company_name}
                onChange={(e) => handleInputChange("company_name", e.target.value)}
                placeholder="Enter company name"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Website</FormLabel>
              <Input
                value={formData.website}
                onChange={(e) => handleInputChange("website", e.target.value)}
                placeholder="https://example.com"
              />
            </FormControl>

            <Divider />

            {/* Contact Information */}
            <Text fontSize="lg" fontWeight="bold">Contact Information</Text>
            <FormControl isRequired>
              <FormLabel>CV Mailing List</FormLabel>
              <Input
                value={formData.cv_mailing_list}
                onChange={(e) => handleInputChange("cv_mailing_list", e.target.value)}
                placeholder="admin@company.com"
                type="email"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Manager Name</FormLabel>
              <Input
                value={formData.manager_name}
                onChange={(e) => handleInputChange("manager_name", e.target.value)}
                placeholder="Enter manager name"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Billing Manager Name</FormLabel>
              <Input
                value={formData.billing_manager_name}
                onChange={(e) => handleInputChange("billing_manager_name", e.target.value)}
                placeholder="Enter billing manager name"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Billing Manager Email</FormLabel>
              <Input
                value={formData.billing_manager_email}
                onChange={(e) => handleInputChange("billing_manager_email", e.target.value)}
                placeholder="billing@company.com"
                type="email"
              />
            </FormControl>

            <Divider />

            {/* Trial Settings */}
            <Text fontSize="lg" fontWeight="bold">Trial Settings</Text>
            <FormControl>
              <FormLabel>Trial Days</FormLabel>
              <NumberInput
                value={formData.trial_days}
                onChange={(value) => handleInputChange("trial_days", parseInt(value) || 0)}
                min={0}
                max={365}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel mb="0">Active Status</FormLabel>
              <Switch
                isChecked={formData.is_active}
                onChange={(e) => handleInputChange("is_active", e.target.checked)}
              />
            </FormControl>

          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="Creating..."
            isDisabled={!formData.company_name || !formData.cv_mailing_list || !formData.manager_name || !formData.billing_manager_name || !formData.billing_manager_email}
          >
            Create Company
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateCompanyModal;
